import React, { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import styled from 'styled-components';
import { Rate } from 'antd';

import fontService, { FontWeight } from 'services/FontService';
import { tablet } from 'services/ui';
import { Colors, ICareerSkillValue } from 'context';
import { ICareerSkillOption } from 'core';
import { Icon, IconNames } from 'atoms';
import { InputWithTags, SkillAutocomplete } from 'molecules';
import { FormFieldset } from 'organisms/ResumeForm';

export interface IProfileSkillsBlock {
  skills: ICareerSkillValue[];
  customSkills: string[];
  onChangeSkills: (skills: ICareerSkillValue[]) => void;
  onChangeCustomSkills: (customSkills: string[]) => void;
}

const DEFAULT_LEVEL = 3;

/**
 * Skills section (spec 0018, increment B). Replaces free-text entry with a
 * dictionary autocomplete: each picked skill gets a 1-5 level. A typed value that
 * matches no dictionary skill drops into the free-text `customSkills` tail.
 */
export const ProfileSkillsBlock = memo(
  ({ skills, customSkills, onChangeSkills, onChangeCustomSkills }: IProfileSkillsBlock) => {
    const { t } = useTranslation();

    const onSelectSkill = useCallback(
      (option: ICareerSkillOption) => {
        if (skills.some((skill) => skill.skillId === option.id)) {
          return;
        }
        onChangeSkills([
          ...skills,
          {
            skillId: option.id,
            name: option.name,
            category: option.category,
            level: DEFAULT_LEVEL,
            years: null,
          },
        ]);
      },
      [skills, onChangeSkills],
    );

    const onFreeText = useCallback(
      (text: string) => {
        if (customSkills.some((skill) => skill.toLowerCase() === text.toLowerCase())) {
          return;
        }
        onChangeCustomSkills([...customSkills, text]);
      },
      [customSkills, onChangeCustomSkills],
    );

    const onChangeLevel = useCallback(
      (skillId: string, level: number) => {
        onChangeSkills(
          skills.map((skill) =>
            skill.skillId === skillId ? { ...skill, level: level || 1 } : skill,
          ),
        );
      },
      [skills, onChangeSkills],
    );

    const onRemoveSkill = useCallback(
      (skillId: string) => {
        onChangeSkills(skills.filter((skill) => skill.skillId !== skillId));
      },
      [skills, onChangeSkills],
    );

    return (
      <FormFieldset groupTitle={t('careerProfile.skills.title')}>
        <SkillAutocomplete
          placeholder={t('careerProfile.skills.searchPlaceholder')}
          onSelectSkill={onSelectSkill}
          onFreeText={onFreeText}
        />

        <SkillList>
          {skills.map((skill) => (
            <SkillRow key={skill.skillId}>
              <SkillName>{skill.name}</SkillName>
              <Rate
                count={5}
                value={skill.level}
                onChange={(level) => onChangeLevel(skill.skillId, level)}
              />
              <RemoveButton onClick={() => onRemoveSkill(skill.skillId)}>
                <Icon name={IconNames.GrayCloseSm} width={20} height={20} />
              </RemoveButton>
            </SkillRow>
          ))}
        </SkillList>

        <CustomLabel>{t('careerProfile.skills.customLabel')}</CustomLabel>
        <InputWithTags
          tagsList={customSkills}
          placeholder={t('careerProfile.skills.customPlaceholder')}
          onChange={onChangeCustomSkills}
        />
      </FormFieldset>
    );
  },
);

const SkillList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 20px 0 24px;
`;

const SkillRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

const SkillName = styled.span`
  min-width: 160px;
  ${fontService.text({ size: 15, lineHeight: 133, weight: FontWeight.medium })}

  ${tablet} {
    min-width: 200px;
  }
`;

const RemoveButton = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;

  &:hover svg > * {
    transition: 0.3s;
    fill: ${Colors.black};
  }
`;

const CustomLabel = styled.p`
  margin-bottom: 4px;
  ${fontService.text({ size: 14, lineHeight: 145, weight: FontWeight.medium })}
`;
